import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@resy/ui-kitchen';
import AmexCenturionEventCard from '../AmexCenturionNYEventCard';
import {
  EVENTS_PER_ROW, EVENTS_LENGTH_6, EVENTS_LENGTH_17, NON_TABBED_MOBILE_EVENTS_LENGTH,
} from '../constants';
import { groupUpcomingEventsBasedOnMonth } from '../AmexCenturionNYDetails';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexCenturionNYUpcomingEvents.scss';

const AmexCenturionNYUpcomingEvents = (
  {
    baseRequestUrl,
    events: upcomingEventList,
    eventButtons,
    matchMedia,
    eventDetails,
    Analytics,
    venue,
  }
) => {
  const eventsLen = upcomingEventList?.length || 0;
  if (eventsLen === 0 || !venue || !eventDetails) return null;
  const [activeIndex, setActiveIndex] = useState(0);

  const {
    title,
    sub_title: subTitle,
    event_layouts: eventLayouts,
    is_sold_out_enabled: isSoldOutEnabled,
    sold_out_label: soldOutLabel,
  } = eventDetails;

  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubTitle = sanitizeString(subTitle);

  if (!title || !eventLayouts || isSoldOutEnabled === undefined
     || (typeof isSoldOutEnabled !== 'boolean')
  ) return null;

  if (isSoldOutEnabled && !soldOutLabel) return null;

  const getEventsDesktopLayout = () => {
    let eventPerRow = 0;
    if (eventsLen < EVENTS_LENGTH_6) {
      eventPerRow = eventsLen;
    } else if (eventsLen > EVENTS_LENGTH_17) {
      eventPerRow = EVENTS_PER_ROW;
    } else {
      eventLayouts.forEach((eventLayout) => {
        eventLayout.number_of_events.forEach((numberOfEvent) => {
          if (numberOfEvent === eventsLen) {
            eventPerRow = eventLayout.events_per_row;
          }
          return null;
        });
      });
    }
    return eventPerRow;
  };

  const getUpcomingEventsMobileLayout = () => {
    let tabbedLayout = false;
    if (eventsLen > NON_TABBED_MOBILE_EVENTS_LENGTH) {
      tabbedLayout = true;
    }
    return tabbedLayout;
  };

  const isDesktop = matchMedia.lg;
  const eventsPerRow = getEventsDesktopLayout();
  const tabbedLayout = getUpcomingEventsMobileLayout() && !isDesktop;

  const [upcomingOneMonthEventsArr, setUpcomingOneMonthEventsArr] = useState([]);
  const [selectedYearMonthId, setSelectedYearMonthId] = useState('');

  const upcomingDataWithMonth = groupUpcomingEventsBasedOnMonth(upcomingEventList);
  const mobileTabbedEvents = Object.values(upcomingDataWithMonth);

  const getInitialLoadData = () => {
    if (mobileTabbedEvents.length > 0) {
      setUpcomingOneMonthEventsArr(mobileTabbedEvents[0]);
      setSelectedYearMonthId(mobileTabbedEvents[0][0].yearMonthId);
    }
  };

  const loadMonthEventData = (monthYearId) => {
    setUpcomingOneMonthEventsArr(upcomingDataWithMonth[monthYearId]);
    setSelectedYearMonthId(monthYearId);
  };

  const handleKeyDown = (keyEvent) => {
    const count = mobileTabbedEvents.length;
    const nextTab = () => setActiveIndex((activeIndex + 1) % count);
    const prevTab = () => setActiveIndex((activeIndex - 1 + count) % count);
    const firstTab = () => setActiveIndex(0);
    const lastTab = () => setActiveIndex(count - 1);
    const keyMap = {
      ArrowRight: nextTab,
      ArrowLeft: prevTab,
      Home: firstTab,
      End: lastTab,
    };

    const action = keyMap[keyEvent.key];
    if (action) {
      action();
    }
  };

  useEffect(() => {
    getInitialLoadData();
  }, []);

  useEffect(() => {
    loadMonthEventData(mobileTabbedEvents[activeIndex][0].yearMonthId);
    if (tabbedLayout) {
      const target = document.getElementsByClassName('AmexCenturionNYUpcomingEvents__grid')[0];
      const activeTab = document.getElementById(`upcomingEventsTab-${activeIndex}`);
      if (target && activeTab) {
        // resets the scroll position in the selected tab
        setTimeout(() => {
          target.scrollTo({ left: 0, behavior: 'smooth' });
          activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 0);
      }
    }
  }, [activeIndex]);

  const renderTabs = () => mobileTabbedEvents
    .map((tab, index) => (
      <Button
        variant="no-style"
        type="button"
        role="tab"
        id={`upcomingEventsTab-${index}`}
        key={`tab-${tab.title}`}
        aria-controls="upcomingEventsPanel-1"
        onClick={() => setActiveIndex(index)}
        onKeyDown={handleKeyDown}
        aria-selected={activeIndex === index}
        className={`AmexCenturionNYUpcomingEvents__tab ${activeIndex === index ? 'active' : ''}`}
        {...(activeIndex !== index && { tabIndex: -1 })}
      >
        {tab[0].monthYearDisplay}
      </Button>
    ));

  const renderTabPanels = () => (
    <div
      role="tabpanel"
      key={`tabpanel-${title}`}
      hidden={!(selectedYearMonthId === upcomingOneMonthEventsArr[0].yearMonthId)}
      className={`AmexCenturionNYUpcomingEvents__tabpanel ${(selectedYearMonthId === upcomingOneMonthEventsArr[0].yearMonthId) ? 'active' : ''}`}
      aria-labelledby={`upcomingEventsTab-${activeIndex}`}
      tabIndex="0"
      id="upcomingEventsPanel-1"
    >
      <div className="AmexCenturionNYUpcomingEvents__content">
        <ul className={`AmexCenturionNYUpcomingEvents__grid layout-multiple list-${eventsPerRow}-cards`}>
          {upcomingOneMonthEventsArr.map((eventItem) => (
            <li key={eventItem.id}>
              <AmexCenturionEventCard
                {...eventItem}
                eventButtons={eventButtons}
                isDesktop={isDesktop}
                baseRequestUrl={baseRequestUrl}
                Analytics={Analytics}
                venue={{
                  ...venue,
                  locality: venue.location.city,
                  neighborhood: venue.location.neighborhood,
                }}
                eventDetails={eventDetails}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderMobileTabbedLayout = () => (
    <>
      <div
        className="AmexCenturionNYUpcomingEvents__tabswrapper"
      >
        <div
          role="tablist"
          className="AmexCenturionNYUpcomingEvents__tabs"
          aria-labelledby="upcomingEventsTitle"
        >
          {renderTabs()}
        </div>
      </div>
      {renderTabPanels()}
    </>
  );

  return (
    <section className="AmexCenturionNYUpcomingEvents">
      <div className="container">
        <h2
          className="AmexCenturionNYUpcomingEvents__title"
          id="upcomingEventsTitle"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedTitle,
          }}
        />
        {subTitle && (
          <h3
            className="AmexCenturionNYUpcomingEvents__subTitle"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSubTitle,
            }}
          />
        )}
        {!tabbedLayout && eventsLen === 1 && (
          <div className="layout-single">
            <AmexCenturionEventCard
              {...upcomingEventList[0]}
              key={upcomingEventList[0].id}
              eventButtons={eventButtons}
              baseRequestUrl={baseRequestUrl}
              Analytics={Analytics}
              venue={{
                ...venue,
                locality: venue.location.city,
                neighborhood: venue.location.neighborhood,
              }}
              eventDetails={eventDetails}
            />
          </div>
        )}
        {!tabbedLayout && eventsLen > 1 && (
          <div className="AmexCenturionNYUpcomingEvents__wrapper">
            <ul className={`AmexCenturionNYUpcomingEvents__grid layout-multiple list-${eventsPerRow}-cards`}>
              {upcomingEventList.map((eventItem) => (
                <li key={eventItem.id}>
                  <AmexCenturionEventCard
                    {...eventItem}
                    eventButtons={eventButtons}
                    isDesktop={isDesktop}
                    baseRequestUrl={baseRequestUrl}
                    Analytics={Analytics}
                    venue={{
                      ...venue,
                      locality: venue.location.city,
                      neighborhood: venue.location.neighborhood,
                    }}
                    eventDetails={eventDetails}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        { (upcomingOneMonthEventsArr.length > 0) && tabbedLayout && renderMobileTabbedLayout() }
      </div>
    </section>
  );
};

AmexCenturionNYUpcomingEvents.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
  })).isRequired,
  eventButtons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  venue: PropTypes.shape({
    locality: PropTypes.string,
    location: PropTypes.shape({
      code: PropTypes.string,
      city: PropTypes.string,
      neighborhood: PropTypes.string.isRequired,
    }),
    name: PropTypes.string.isRequired,
    urlSlug: PropTypes.string.isRequired,
  }).isRequired,
  eventDetails: PropTypes.shape({
    title: PropTypes.string,
    sub_title: PropTypes.string,
    is_sold_out_enabled: PropTypes.bool,
    sold_out_label: PropTypes.string,
    event_layouts: PropTypes.arrayOf(PropTypes.shape({
      event_per_row: PropTypes.number,
      number_of_events: PropTypes.arrayOf(PropTypes.number),
    })),
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

export default AmexCenturionNYUpcomingEvents;
